import { yupResolver } from '@hookform/resolvers/yup/dist/yup.js'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Skeleton as _Skeleton,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SkeletonProps,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers-pro'
import dayjs from 'dayjs'
import { Controller, useForm, useWatch } from 'react-hook-form'
import * as yup from 'yup'
import { type CustomerPlanDiscount, DiscountModifier, DiscountType } from '../api/api'
import { BorderedStack } from '../components'
import { Loader } from '../components/Loader'

interface CreateFlatAndPerUnitDiscount {
  modifier: DiscountModifier
  value: number | string
  adornment: string
  expirationDate: string | undefined
  type: DiscountType
}

const defaultedFormValues = (discount: CustomerPlanDiscount | undefined): CreateFlatAndPerUnitDiscount => {
  if (!discount) {
    return {
      modifier: DiscountModifier.PERCENTAGE,
      value: '',
      adornment: '%',
      expirationDate: undefined,
      type: DiscountType.PER_UNIT_AND_FLAT_DISCOUNT,
    }
  }

  switch (discount.discount.modifier) {
    case DiscountModifier.PERCENTAGE: {
      return {
        modifier: DiscountModifier.PERCENTAGE,
        value: discount.discount.percentage,
        adornment: '%',
        expirationDate: discount.expirationDate,
        type: DiscountType.PER_UNIT_AND_FLAT_DISCOUNT,
      }
    }

    case DiscountModifier.ABSOLUTE: {
      return {
        modifier: DiscountModifier.ABSOLUTE,
        value: discount.discount.amount.value,
        adornment: discount.discount.amount.currency,
        expirationDate: discount.expirationDate,
        type: DiscountType.FLAT_DISCOUNT,
      }
    }
  }
}

export interface CustomerSubscriptionPlanProps {
  discount: CustomerPlanDiscount | undefined
  onCreateDiscount: (flatAndPerUnitDiscount: CustomerPlanDiscount) => void
  onDeleteDiscount: () => void
  currency: string
  isLoading: boolean
  isPatching: boolean
  isDeleting: boolean
}

export const CreateCustomerPlanDiscount = ({
  discount,
  onCreateDiscount,
  onDeleteDiscount,
  currency,
  isLoading,
  isPatching,
  isDeleting,
}: CustomerSubscriptionPlanProps) => {
  const form = useForm<CreateFlatAndPerUnitDiscount>({
    values: defaultedFormValues(discount),
    resolver: yupResolver(schema),
  })

  const adornment = useWatch({ control: form.control, name: 'adornment' })

  const handleSubmit = (data: CreateFlatAndPerUnitDiscount) => {
    onCreateDiscount(mapFormValuesToCustomerPlanDiscount(data))
  }

  const updateAdornment = (modifier: DiscountModifier) => {
    switch (modifier) {
      case DiscountModifier.PERCENTAGE: {
        return form.setValue('adornment', '%')
      }

      case DiscountModifier.ABSOLUTE: {
        return form.setValue('adornment', currency)
      }
    }
  }

  return (
    <BorderedStack
      sx={{
        flex: 1,
        gap: 3,
        padding: 2,
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: 1,
          alignItems: 'center',
        }}
      >
        <Typography variant="p2Medium">Total invoice discount</Typography>
        <Tooltip title="Applies a discount to the total invoice amount seperately from a product discount" placement="top">
          <Typography
            color="textSecondary"
            sx={{
              lineHeight: 0,
            }}
          >
            <InfoOutlinedIcon fontSize="inherit" />
          </Typography>
        </Tooltip>
      </Stack>
      <Stack
        sx={{
          flex: 1,
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Loader isLoading={isLoading} loader={FormSkeleton}>
            <Stack
              direction="row"
              sx={{
                alignItems: 'baseline',
                flex: 1,
                gap: 2,
              }}
            >
              {/* Locked to percentage: absolute (fixed) discounts are not implemented in the backend yet */}
              <Controller
                control={form.control}
                name="modifier"
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Discount type (Only percentage available)</InputLabel>
                    <Select
                      fullWidth
                      disabled
                      label="Discount type (Only percentage available)"
                      slotProps={{ input: { 'aria-label': 'discount-modifier' } }}
                      onChange={(a) => {
                        updateAdornment(a.target.value as DiscountModifier)
                        field.onChange(a.target.value as DiscountModifier)
                      }}
                      value={field.value}
                    >
                      <MenuItem value={DiscountModifier.ABSOLUTE}>Fixed</MenuItem>
                      <MenuItem value={DiscountModifier.PERCENTAGE}>Percentage</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
              <Controller
                control={form.control}
                name="value"
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Discount value"
                    error={!!form.formState.errors.value}
                    helperText={form.formState.errors.value?.message}
                    slotProps={{
                      input: {
                        endAdornment: adornment,
                      },
                    }}
                  />
                )}
              />
            </Stack>
            <Controller
              control={form.control}
              name="expirationDate"
              render={({ field }) => (
                <DatePicker
                  {...field}
                  minDate={dayjs()}
                  value={field.value ? dayjs(field.value) : null}
                  label="Expiration date"
                  onChange={(date) => form.setValue('expirationDate', date?.format('YYYY-MM-DD'))}
                />
              )}
            />
          </Loader>
        </Stack>
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Button
            size="medium"
            loading={isPatching}
            disabled={isLoading || !form.formState.isDirty}
            variant="contained"
            type="submit"
            onClick={form.handleSubmit(handleSubmit)}
          >
            Save
          </Button>
          <Button
            size="medium"
            loading={isDeleting}
            disabled={isLoading || !discount}
            variant="text"
            color="error"
            type="button"
            onClick={onDeleteDiscount}
          >
            Delete discount
          </Button>
        </Stack>
      </Stack>
    </BorderedStack>
  )
}

const Skeleton = (props: SkeletonProps) => <_Skeleton {...props} sx={{ transform: 'none' }} />

const FormSkeleton = () => (
  <Stack
    sx={{
      gap: 2,
    }}
  >
    <Stack
      direction="row"
      sx={{
        gap: 2,
      }}
    >
      <Skeleton width="100%" height={48.75} />
      <Skeleton width="100%" height={48.75} />
    </Stack>
    <Skeleton width="100%" height={48} />
  </Stack>
)

const schema = yup
  .object({
    modifier: yup.mixed<DiscountModifier>().oneOf(Object.values(DiscountModifier)).required(),
    value: yup.number().typeError('Value must be a number').positive('Value must be positive').required('Value is required'),
    adornment: yup.string().required(),
  })
  .required()

const mapFormValuesToCustomerPlanDiscount = (data: CreateFlatAndPerUnitDiscount): CustomerPlanDiscount => {
  switch (data.modifier) {
    case DiscountModifier.PERCENTAGE: {
      return {
        type: DiscountType.PER_UNIT_AND_FLAT_DISCOUNT,
        discount: {
          modifier: DiscountModifier.PERCENTAGE,
          percentage: +data.value,
        },
        expirationDate: data.expirationDate,
      }
    }

    case DiscountModifier.ABSOLUTE: {
      return {
        type: DiscountType.FLAT_DISCOUNT,
        discount: {
          modifier: DiscountModifier.ABSOLUTE,
          amount: {
            currency: data.adornment,
            value: +data.value,
          },
        },
        expirationDate: data.expirationDate,
      }
    }
  }
}
